import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, InferType } from "yup";
import {
  useGetJWTForAccount,
  useLogin,
  useRelatedCustomers,
} from "@/requests/hooks/user";
import { Input } from "@/components/Input";
import { Button, PasswordSwitcherButton } from "@/components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignPostsWrench,
  faTriangleExclamation,
} from "@fortawesome/pro-duotone-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/Form";
import parseQueryString from "@/utils/parseQueryString";
import { H3 } from "@/components/Typography";
import formatError from "@/utils/formatError";
import { t } from "i18next";
import { useQueryClient } from "@tanstack/react-query";
import getCookie from "@/utils/getCookie";
import { INTEGRATIONS_CHECKLIST_URL } from "@/utils/const";

const formSchema = object({
  email: string()
    .email(t("auth.login.form.errors.emailInvalid"))
    .required(t("auth.login.form.errors.emailRequired")),
  password: string().required(t("auth.login.form.errors.passwordRequired")),
});

type ILoginForm = InferType<typeof formSchema>;

const Login = () => {
  const navigate = useNavigate();
  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginForm>({
    resolver: yupResolver(formSchema),
  });
  const queryClient = useQueryClient();
  const {
    mutateAsync: login,
    data: loginData,
    error: loginError,
    isPending: isLoginPending,
    isSuccess: isLoginSuccess,
  } = useLogin();

  const {
    isFetching: isFetchingGetRelatedCustomers,
    isSuccess: isGetRelatedCustomersSuccess,
    data: relatedCustomers,
  } = useRelatedCustomers({
    enabled: !!loginData,
  });

  const { mutate: refreshToken, isSuccess: isRefreshTokenSuccess } =
    useGetJWTForAccount();

  const handleNavigation = () => {
    const { redirect } = parseQueryString();

    if (
      !!relatedCustomers?.results?.length &&
      relatedCustomers?.results?.length > 1
    ) {
      const companySelectorUrl = redirect
        ? `/auth/company-selector?redirect=${encodeURIComponent(redirect)}`
        : "/auth/company-selector";
      navigate(companySelectorUrl);
    } else if (redirect) {
      navigate(redirect);
    } else {
      navigate(INTEGRATIONS_CHECKLIST_URL);
    }
  };

  useEffect(() => {
    if (!isLoginSuccess || !isGetRelatedCustomersSuccess) return;

    /**
     * When a user is installing a second store, we log them out and
     * have them re-authenticate via the integration-installation flow
     * so we can add that store to that user. We need to trigger the
     * `refresh-token` for this to happen, but we should do this at
     * log in time, and not further on, so we don't interrupt the user.
     */
    const existingUserCookie = getCookie("ds-existing-user");
    if (!!loginData?.activeOnboardingFlow && !!existingUserCookie) {
      /**
       * We know the customer is in onboarding, and that the OAUTH flow
       * has logged them out from an existing session (the ds-existing-user
       * cookie is only set in this circumstance).
       */
      refreshToken({
        merchant_id: loginData?.merchantId,
        integration: "?integration=SHOPIFY",
      });

      return;
    }

    handleNavigation();
  }, [isGetRelatedCustomersSuccess, isLoginSuccess]);

  useEffect(() => {
    if (isRefreshTokenSuccess) {
      /**
       * We want to hard redirect the user, so that the page is reloaded and
       * the latest auth-status call gets the correct onboarding state.
       */
      window.location.replace("/onboarding");
    }
  }, [isRefreshTokenSuccess]);

  const onSubmit = async ({ email, password }: ILoginForm) => {
    const data = await login({ username: email, password });
    queryClient.setQueryData(["user"], data);
  };

  const toggleInputType = () =>
    setShowPasswordInput(setShowPassword => !setShowPassword);

  const renderHeader = () => (
    <div className="text-center">
      <H3 className="mb-10 font-semibold">{t("auth.login.title")}</H3>
      <p className="mb-20 text-gray-dark">{t("auth.login.description")}</p>
    </div>
  );

  const renderShopifyInstallationAlert = () => (
    <div className="mb-30 flex items-start gap-20 rounded bg-orange/20 p-20 text-orange-dark">
      <FontAwesomeIcon fixedWidth icon={faSignPostsWrench} className="mt-10" />
      <div>
        <p className="font-bold">
          {t("auth.login.alerts.installationInProgress.title")}
        </p>
        {t("auth.login.alerts.installationInProgress.description")}
      </div>
    </div>
  );

  const renderAlert = () => {
    const { auth_message, origin } = parseQueryString();

    if (!!origin) {
      return renderShopifyInstallationAlert();
    }

    if (loginError || auth_message) {
      const { message } = formatError(loginError);

      return (
        <div className="mb-30 flex items-center gap-20 rounded bg-red/5 p-20 text-red">
          <FontAwesomeIcon fixedWidth icon={faTriangleExclamation} />
          {auth_message === "session_timeout"
            ? t("auth.login.alerts.sessionTimeout")
            : !!message
              ? message
              : t("generic.error")}
        </div>
      );
    }
  };

  const renderPasswordSwitcher = () => (
    <PasswordSwitcherButton
      showPassword={showPasswordInput}
      onClick={toggleInputType}
    />
  );

  const renderForm = () => {
    const { ref, ...rest } = register("password");

    const handleSharedRef = (e: HTMLInputElement | null) => {
      ref(e);
      passwordRef.current = e;
    };

    return (
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          label={t("auth.login.form.email")}
          type="email"
          {...register("email")}
          helperText={errors?.email?.message}
          variant={errors?.email && "error"}
          testId="email-input"
          aria-invalid={!!errors?.email}
        />

        <Input
          label={t("auth.login.form.password")}
          type={showPasswordInput ? "text" : "password"}
          {...rest}
          helperText={errors?.password?.message}
          variant={errors?.password && "error"}
          ref={handleSharedRef}
          trailingSlot={renderPasswordSwitcher()}
          testId="password-input"
          aria-invalid={!!errors?.password}
        />

        <div className="flex w-full justify-end">
          <Button as="link" to="/auth/forgot-password" variant="link-light">
            {t("auth.login.form.forgotPassword")}
          </Button>
        </div>

        <Button
          variant="primary"
          type="submit"
          width="unset"
          loading={isLoginPending || isFetchingGetRelatedCustomers}
          data-testid="login-cta">
          {t("auth.login.form.logIn")}
        </Button>
      </Form>
    );
  };

  return (
    <>
      {renderHeader()}
      {renderAlert()}
      {renderForm()}
    </>
  );
};

export default Login;
